// The icons a customer can pick for their assistant in the dashboard's
// Customize tab — shown on the launcher bubble, in the panel header, and
// as a new bot's default. Shared between the widget (the gallery) and the
// server (the default), so the two can never disagree about a byte.
//
// Every icon is a small inline SVG turned into a data: URI, so there is
// nothing to upload or host and the server never fetches a third-party
// image. Three kinds:
//
//  - Agents: illustrated support agents wearing a headset — what every
//    support widget's avatar gallery converges on, because a visitor
//    instantly reads "someone here will help me". Drawn from parameters
//    (hair, headwear, expression, glasses, headset type, framing,
//    background treatment) so the set covers a lot of ground while
//    keeping one consistent style.
//  - Mascots: animals and robots wearing the same headset, for a brand
//    with a lighter tone, or one that would rather not put a human face
//    on an automated assistant.
//  - Symbols: a headset, a chat bubble, a lifebuoy, a bolt, a lightbulb…
//    for a brand that wants a glyph rather than a character.
//
// Sizes to design for: 128px on the launcher (where a face has to look
// pleasant), 48px in the gallery, and only 22px in the panel header,
// where just the silhouette and the headset still have to read.
//
// LENGTH LIMIT: the dashboard API truncates logo URLs at 4000 characters
// (server/src/http/routes/dashboard.ts), after which an SVG is a broken
// image. server/src/bots/__tests__/avatars.test.ts asserts every icon
// here stays well under that.

export type HairStyle =
  | 'short'
  | 'long'
  | 'curly'
  | 'buzz'
  | 'bun'
  | 'hijab'
  | 'afro'
  | 'spiky'
  | 'ponytail'
  | 'bob'
  | 'bald'
  | 'wavy'
  | 'braids'
  | 'sidepart';
export type Headwear = 'cap' | 'beanie' | 'headband' | 'turban';
export type Glasses = 'round' | 'square' | 'sun';
export type Mouth = 'smile' | 'grin' | 'laugh' | 'neutral' | 'wink';
export type Collar = 'v' | 'tie' | 'bow' | 'lanyard' | 'none';
export type BgStyle = 'solid' | 'gradient' | 'ring' | 'dots';

export interface AgentAvatarOptions {
  skin: string;
  /** Hair colour — or the scarf/turban colour for `hijab`/`turban`. */
  hair: string;
  style: HairStyle;
  shirt: string;
  bg: string;
  /** Second colour for a `gradient` background; defaults to a lighter bg. */
  bg2?: string;
  bgStyle?: BgStyle;
  hat?: Headwear;
  /** Hat colour; defaults to the shirt colour. */
  hatColor?: string;
  glasses?: Glasses;
  beard?: boolean;
  mustache?: boolean;
  mouth?: Mouth;
  /** Single-ear call-centre headset instead of the full band. */
  headset?: 'full' | 'single';
  earrings?: boolean;
  collar?: Collar;
  /** Bigger face, less shoulder — reads better at 22px. */
  closeup?: boolean;
}

const INK = '#1e293b';
const HALO = '#ffffff';

function hairPaths(o: AgentAvatarOptions): { back: string; front: string } {
  const h = o.hair;
  const cap = `<path d="M19 25C19 15 25 11 32 11s13 4 13 14c-3-6-8-8-13-8s-10 2-13 8z" fill="${h}"/>`;
  switch (o.style) {
    case 'short':
      return { back: '', front: cap };
    case 'bald':
      return { back: '', front: '' };
    case 'buzz':
      return { back: '', front: `<path d="M20 22c1-8 6-10.5 12-10.5S43 14 44 22c-4-5-8-6-12-6s-8 1-12 6z" fill="${h}"/>` };
    case 'sidepart':
      return { back: '', front: `<path d="M19 26c0-11 6-15 13-15c8 0 14 4 13 13c-5-6-12-5-18-3c-3 1-6 3-8 5z" fill="${h}"/>` };
    case 'spiky':
      return { back: '', front: cap + `<path d="M20 17l3-7 3 6 3-8 3 7 3-8 3 7 3-6 3 7 2-5 1 8z" fill="${h}"/>` };
    case 'long':
      return { back: `<path d="M19 22c-3 7-4 14-2 23c3-2 5-4 5-8V26z M45 22c3 7 4 14 2 23c-3-2-5-4-5-8V26z" fill="${h}"/>`, front: cap };
    case 'wavy':
      return {
        back: `<path d="M19 23c-4 6-5 13-4 20c2 1 4-1 5 1c1-5 2-10 2-16z M45 23c4 6 5 13 4 20c-2 1-4-1-5 1c-1-5-2-10-2-16z" fill="${h}"/>`,
        front: cap + `<path d="M20 20c2-3 5-3 7-1c-2-1-4 2-7 1z M37 19c2-2 5-2 7 1c-3 1-5-2-7-1z" fill="${h}"/>`,
      };
    case 'bob':
      return {
        back: `<path d="M18 24c-2 6-2 12 0 16h8V26z M46 24c2 6 2 12 0 16h-8V26z" fill="${h}"/>`,
        front: `<path d="M18 26c0-11 7-15 14-15s14 4 14 15c-2-4-6-6-10-6c-2 3-5 4-8 4c-4 0-7 1-10 2z" fill="${h}"/>`,
      };
    case 'bun':
      return { back: `<circle cx="32" cy="9" r="4.5" fill="${h}"/>`, front: cap };
    case 'ponytail':
      return {
        back: `<path d="M42 14c6 4 8 14 5 24c-2-2-3-4-3-7c0-5-1-9-4-13z" fill="${h}"/>`,
        front: cap,
      };
    case 'braids':
      return {
        back: `<g fill="${h}"><path d="M20 24c-3 8-3 14-1 22l4-1c-1-7-1-13 0-19z M44 24c3 8 3 14 1 22l-4-1c1-7 1-13 0-19z"/></g><g fill="none" stroke="${o.bg}" stroke-width="1"><path d="M19 30h5M19 35h5M19 40h5M40 30h5M40 35h5M40 40h5"/></g>`,
        front: cap,
      };
    case 'curly':
      return {
        back: '',
        front:
          cap +
          `<g fill="${h}"><circle cx="22" cy="18" r="5"/><circle cx="28" cy="13.5" r="5.5"/><circle cx="36" cy="13" r="5.5"/><circle cx="42" cy="18" r="5"/><circle cx="19" cy="24" r="4.5"/><circle cx="45" cy="24" r="4.5"/></g>`,
      };
    case 'afro':
      return { back: `<circle cx="32" cy="20" r="17" fill="${h}"/>`, front: '' };
    case 'hijab':
      return {
        back: `<path d="M32 8C20 8 14 19 15 31c0 9-3 15-6 23h46c-3-8-6-14-6-23C50 19 44 8 32 8z" fill="${h}"/>`,
        front: '',
      };
  }
}

function hatPaths(o: AgentAvatarOptions): string {
  const c = o.hatColor ?? o.shirt;
  switch (o.hat) {
    case 'cap':
      return `<path d="M18 22c0-9 6-13 14-13s14 4 14 13z" fill="${c}"/><path d="M14 22.5c6-2 30-2 36 0c-6 2-30 2-36 0z" fill="${c}"/><circle cx="32" cy="9.5" r="1.5" fill="${c}"/>`;
    case 'beanie':
      return `<path d="M18 23c0-10 6-14 14-14s14 4 14 14z" fill="${c}"/><rect x="17" y="20" width="30" height="5" rx="2" fill="${c}" stroke="${HALO}" stroke-width=".8"/><circle cx="32" cy="8.5" r="3" fill="${HALO}"/>`;
    case 'headband':
      return `<path d="M19.5 20c3-3 8-4.5 12.5-4.5s9.5 1.5 12.5 4.5c-3-1.5-8-2-12.5-2s-9.5.5-12.5 2z" fill="${c}"/>`;
    case 'turban':
      return `<path d="M18 25c0-11 6-16 14-16s14 5 14 16c-3-4-8-5-14-5s-11 1-14 5z" fill="${c}"/><path d="M22 16c3-4 7-6 10-4c-4 0-7 2-10 4z" fill="${HALO}" opacity=".35"/>`;
    default:
      return '';
  }
}

function bgParts(o: AgentAvatarOptions): string {
  const style = o.bgStyle ?? 'solid';
  const disc = `<circle cx="32" cy="32" r="32" fill="${o.bg}"/>`;
  switch (style) {
    case 'gradient':
      return `<defs><linearGradient id="g" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="${o.bg}"/><stop offset="1" stop-color="${o.bg2 ?? HALO}"/></linearGradient></defs><circle cx="32" cy="32" r="32" fill="url(#g)"/>`;
    case 'ring':
      return disc + `<circle cx="32" cy="32" r="29" fill="none" stroke="${o.shirt}" stroke-width="2.5"/>`;
    case 'dots':
      return (
        disc +
        `<g fill="${o.shirt}" opacity=".18"><circle cx="12" cy="18" r="2"/><circle cx="52" cy="14" r="2"/><circle cx="8" cy="36" r="2"/><circle cx="56" cy="34" r="2"/><circle cx="16" cy="52" r="2"/><circle cx="50" cy="50" r="2"/></g>`
      );
    default:
      return disc;
  }
}

/** The SVG markup for one agent avatar, 64x64. */
export function agentAvatarSvg(o: AgentAvatarOptions): string {
  const hair = hairPaths(o);
  const headset = o.headset ?? 'full';
  const mouth = o.mouth ?? 'smile';
  const collar = o.collar ?? 'v';
  const faceRadius = o.style === 'hijab' ? 11.5 : 13;
  // A close-up scales the drawing about the face and pushes it down so the
  // shoulders mostly leave the disc.
  const transform = o.closeup ? ' transform="translate(-9.6 -7.6) scale(1.3)"' : '';

  const eyes =
    mouth === 'laugh'
      ? `<g fill="none" stroke="${INK}" stroke-width="1.7" stroke-linecap="round"><path d="M24.5 27.5q2.5-3 5 0M34.5 27.5q2.5-3 5 0"/></g>`
      : mouth === 'wink'
        ? `<circle cx="27" cy="27" r="1.7" fill="${INK}"/><path d="M34.5 27.3q2.5-2 5 0" stroke="${INK}" stroke-width="1.7" fill="none" stroke-linecap="round"/>`
        : `<g fill="${INK}"><circle cx="27" cy="27" r="1.7"/><circle cx="37" cy="27" r="1.7"/></g>`;
  const mouthPath =
    mouth === 'grin' || mouth === 'laugh'
      ? `<path d="M26.5 32c2 5 9 5 11 0z" fill="${INK}"/><path d="M28.5 34.2c2 1.4 5 1.4 7 0z" fill="#f28b82"/>`
      : mouth === 'neutral'
        ? `<path d="M28.5 33.5h7" stroke="${INK}" stroke-width="1.7" stroke-linecap="round"/>`
        : `<path d="M27.5 32.5q4.5 4 9 0" stroke="${INK}" stroke-width="1.7" fill="none" stroke-linecap="round"/>`;
  const glasses =
    o.glasses === 'round'
      ? `<g fill="none" stroke="${INK}" stroke-width="1.5"><circle cx="27" cy="27" r="4.3"/><circle cx="37" cy="27" r="4.3"/><path d="M31.3 27h1.4"/></g>`
      : o.glasses === 'square'
        ? `<g fill="none" stroke="${INK}" stroke-width="1.5"><rect x="22.5" y="23.5" width="8.5" height="7" rx="1.5"/><rect x="33" y="23.5" width="8.5" height="7" rx="1.5"/><path d="M31 26.5h2"/></g>`
        : o.glasses === 'sun'
          ? `<g fill="${INK}"><rect x="22" y="23.5" width="9" height="7" rx="2.5"/><rect x="33" y="23.5" width="9" height="7" rx="2.5"/><path d="M31 26h2v1.5h-2z"/></g>`
          : '';
  const collarPart =
    collar === 'tie'
      ? `<path d="M26.5 42l5.5 6 5.5-6z" fill="${HALO}" opacity=".9"/><path d="M30.5 44l1.5 2 1.5-2 1 9-2.5 3-2.5-3z" fill="${INK}"/>`
      : collar === 'bow'
        ? `<path d="M26.5 42l5.5 5 5.5-5z" fill="${HALO}" opacity=".9"/><path d="M25 44l6 2.5-6 2.5z M39 44l-6 2.5 6 2.5z" fill="${INK}"/><circle cx="32" cy="46.5" r="1.5" fill="${INK}"/>`
        : collar === 'lanyard'
          ? `<path d="M26.5 42l5.5 6 5.5-6z" fill="${HALO}" opacity=".9"/><path d="M27 43l5 11M37 43l-5 11" stroke="${HALO}" stroke-width="1.3"/><rect x="28.5" y="53" width="7" height="8" rx="1" fill="${HALO}"/>`
          : collar === 'none'
            ? ''
            : `<path d="M26.5 42l5.5 6 5.5-6z" fill="${HALO}" opacity=".9"/>`;
  const headsetPart =
    headset === 'single'
      ? // A boom mic on the right ear only, held by a thin band over the head.
        `<g id="hs" fill="none" stroke-linecap="round"><path d="M18.5 27C18.5 16.5 24.5 11 32 11s13.5 5.5 13.5 16" stroke="${HALO}" stroke-width="3.6"/><path d="M18.5 27C18.5 16.5 24.5 11 32 11s13.5 5.5 13.5 16" stroke="${INK}" stroke-width="1.6"/><path d="M45.5 31c.5 6-2.5 9.5-7 9.5" stroke="${INK}" stroke-width="2.4"/></g><rect x="42" y="23" width="6.5" height="10" rx="3" fill="${INK}" stroke="${HALO}" stroke-width="1.2"/><circle cx="38.5" cy="40.5" r="2.3" fill="${INK}"/>`
      : `<g id="hs" fill="none" stroke-linecap="round"><path d="M18.5 27C18.5 16.5 24.5 11 32 11s13.5 5.5 13.5 16" stroke="${HALO}" stroke-width="5.2"/><path d="M18.5 27C18.5 16.5 24.5 11 32 11s13.5 5.5 13.5 16" stroke="${INK}" stroke-width="3"/><path d="M45.5 32c.5 6-2.5 9.5-7 9.5" stroke="${INK}" stroke-width="2.4"/></g><g fill="${INK}" stroke="${HALO}" stroke-width="1.2"><rect x="15.5" y="23" width="6.5" height="10" rx="3"/><rect x="42" y="23" width="6.5" height="10" rx="3"/></g><circle cx="38.5" cy="41.5" r="2.3" fill="${INK}"/>`;

  const parts = [
    bgParts(o),
    `<clipPath id="c"><circle cx="32" cy="32" r="32"/></clipPath>`,
    `<g clip-path="url(#c)"><g${transform}>`,
    o.style === 'hijab' ? '' : hair.back,
    `<rect x="27.5" y="33" width="9" height="12" rx="2.5" fill="${o.skin}"/>`,
    `<path d="M10 66c0-18 10-24 22-24s22 6 22 24z" fill="${o.shirt}"/>`,
    o.style === 'hijab' ? hair.back : collarPart,
    `<circle cx="32" cy="26" r="${faceRadius}" fill="${o.skin}"/>`,
    o.earrings ? `<g fill="#f4c542"><circle cx="19.5" cy="33.5" r="1.6"/><circle cx="44.5" cy="33.5" r="1.6"/></g>` : '',
    o.beard ? `<path d="M21 28c0 9 5 13 11 13s11-4 11-13c-2 7-6 9-11 9s-9-2-11-9z" fill="${o.hair}"/>` : '',
    hair.front,
    hatPaths(o),
    eyes,
    o.mustache ? `<path d="M26.5 31c2-1.5 4-1 5.5 0c1.5-1 3.5-1.5 5.5 0c-2 1.5-4 1.5-5.5.5c-1.5 1-3.5 1-5.5-.5z" fill="${o.hair}"/>` : '',
    mouthPart(mouthPath, o),
    `<g fill="#f28b82" opacity=".35"><circle cx="23.5" cy="31" r="2.2"/><circle cx="40.5" cy="31" r="2.2"/></g>`,
    glasses,
    headsetPart,
    `</g></g>`,
  ];
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64">${parts.join('')}</svg>`;
}

function mouthPart(path: string, o: AgentAvatarOptions): string {
  // A moustache sits where a neutral mouth line would — move the line down.
  return o.mustache ? path.replace('h7', 'h6').replace('33.5', '35') : path;
}

/** A disc with a glyph on it, 64x64. */
function symbolSvg(bg: string, glyph: string): string {
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64"><circle cx="32" cy="32" r="32" fill="${bg}"/>${glyph}</svg>`;
}

export function svgDataUrl(svg: string): string {
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

export type IconKind = 'agent' | 'mascot' | 'symbol';

export interface AssistantIcon {
  id: string;
  /** For the tile's tooltip and screen-reader label. */
  label: string;
  kind: IconKind;
  url: string;
}

export const ICON_KIND_LABELS: Record<IconKind, string> = {
  agent: 'Support agents',
  mascot: 'Mascots',
  symbol: 'Symbols',
};

// Palette — chosen so every combination below keeps the face readable
// against its disc and the headset readable against the hair.
const SKIN = { light: '#f8d9c4', fair: '#efb996', tan: '#f1c27d', medium: '#c68642', deep: '#8d5524', dark: '#5c3a21', olive: '#d8a573' };
const HAIR = { black: '#2b2118', brown: '#5a3a22', auburn: '#a3692b', blonde: '#d9a441', grey: '#8e8e93', white: '#e5e7eb', red: '#c0392b', blue: '#3b82f6', pink: '#ec4899', teal: '#14b8a6' };
const SHIRT = { indigo: '#4f46e5', sky: '#0ea5e9', emerald: '#10b981', amber: '#f59e0b', pink: '#ec4899', slate: '#334155', violet: '#7c3aed', red: '#ef4444', teal: '#0d9488', navy: '#1e3a8a', orange: '#f97316', lime: '#65a30d' };
const BG = { blue: '#dbeafe', pink: '#fce7f3', green: '#d1fae5', amber: '#fef3c7', violet: '#ede9fe', peach: '#ffedd5', sky: '#e0f2fe', grey: '#f1f5f9', mint: '#ccfbf1', rose: '#ffe4e6', lemon: '#fef9c3', lavender: '#e9d5ff' };

type AgentSpec = { id: string; label: string } & AgentAvatarOptions;

const AGENTS: AgentSpec[] = [
  { id: 'agent-1', label: 'Agent with long brown hair, indigo shirt', skin: SKIN.fair, hair: HAIR.brown, style: 'long', shirt: SHIRT.indigo, bg: BG.blue },
  { id: 'agent-2', label: 'Agent with short dark hair, emerald shirt', skin: SKIN.light, hair: HAIR.black, style: 'short', shirt: SHIRT.emerald, bg: BG.pink },
  { id: 'agent-3', label: 'Agent with curly dark hair, amber shirt', skin: SKIN.medium, hair: HAIR.black, style: 'curly', shirt: SHIRT.amber, bg: BG.green },
  { id: 'agent-4', label: 'Agent with a beard, sky-blue shirt', skin: SKIN.deep, hair: HAIR.black, style: 'buzz', shirt: SHIRT.sky, bg: BG.amber, beard: true },
  { id: 'agent-5', label: 'Agent with hair in a bun, pink shirt', skin: SKIN.dark, hair: HAIR.black, style: 'bun', shirt: SHIRT.pink, bg: BG.violet },
  { id: 'agent-6', label: 'Agent with long blonde hair and glasses, slate shirt', skin: SKIN.light, hair: HAIR.blonde, style: 'long', shirt: SHIRT.slate, bg: BG.sky, glasses: 'round' },
  { id: 'agent-7', label: 'Agent wearing a violet headscarf', skin: SKIN.tan, hair: SHIRT.violet, style: 'hijab', shirt: SHIRT.violet, bg: BG.peach },
  { id: 'agent-8', label: 'Agent with short auburn hair and glasses, red shirt', skin: SKIN.fair, hair: HAIR.auburn, style: 'short', shirt: SHIRT.red, bg: BG.grey, glasses: 'round' },
  { id: 'agent-9', label: 'Agent with long dark hair, indigo shirt', skin: SKIN.medium, hair: HAIR.black, style: 'long', shirt: SHIRT.indigo, bg: BG.amber },
  { id: 'agent-10', label: 'Agent with grey hair and a beard, emerald shirt', skin: SKIN.light, hair: HAIR.grey, style: 'short', shirt: SHIRT.emerald, bg: BG.blue, beard: true },
  { id: 'agent-11', label: 'Agent with curly hair and glasses, amber shirt', skin: SKIN.dark, hair: HAIR.black, style: 'curly', shirt: SHIRT.amber, bg: BG.pink, glasses: 'round' },
  { id: 'agent-12', label: 'Agent with red hair in a bun, sky-blue shirt', skin: SKIN.fair, hair: HAIR.red, style: 'bun', shirt: SHIRT.sky, bg: BG.green },
  // ---- the wider range -------------------------------------------------
  { id: 'agent-13', label: 'Agent with an afro, laughing, teal shirt', skin: SKIN.dark, hair: HAIR.black, style: 'afro', shirt: SHIRT.teal, bg: BG.lemon, mouth: 'laugh' },
  { id: 'agent-14', label: 'Agent with a blue mohawk and sunglasses', skin: SKIN.light, hair: HAIR.blue, style: 'spiky', shirt: SHIRT.slate, bg: BG.rose, glasses: 'sun', mouth: 'grin', headset: 'single' },
  { id: 'agent-15', label: 'Agent wearing a turban, navy shirt', skin: SKIN.medium, hair: HAIR.black, style: 'bald', hat: 'turban', hatColor: SHIRT.orange, shirt: SHIRT.navy, bg: BG.mint, beard: true },
  { id: 'agent-16', label: 'Agent with a ponytail, winking, orange shirt', skin: SKIN.fair, hair: HAIR.auburn, style: 'ponytail', shirt: SHIRT.orange, bg: BG.lavender, mouth: 'wink', earrings: true },
  { id: 'agent-17', label: 'Bald agent with a moustache and a tie', skin: SKIN.light, hair: HAIR.grey, style: 'bald', shirt: SHIRT.navy, bg: BG.grey, mustache: true, collar: 'tie', mouth: 'neutral' },
  { id: 'agent-18', label: 'Agent with a bob cut, square glasses, lanyard', skin: SKIN.tan, hair: HAIR.black, style: 'bob', shirt: SHIRT.teal, bg: BG.peach, glasses: 'square', collar: 'lanyard' },
  { id: 'agent-19', label: 'Agent in a baseball cap, grinning', skin: SKIN.deep, hair: HAIR.black, style: 'buzz', hat: 'cap', hatColor: SHIRT.red, shirt: SHIRT.lime, bg: BG.sky, mouth: 'grin', headset: 'single' },
  { id: 'agent-20', label: 'Agent with braids and a bow tie', skin: SKIN.dark, hair: HAIR.black, style: 'braids', shirt: SHIRT.pink, bg: BG.lemon, collar: 'bow', earrings: true },
  { id: 'agent-21', label: 'Agent with wavy pink hair, close-up', skin: SKIN.light, hair: HAIR.pink, style: 'wavy', shirt: SHIRT.violet, bg: BG.mint, closeup: true, bgStyle: 'gradient', bg2: '#ffffff' },
  { id: 'agent-22', label: 'Agent in a beanie, close-up, winking', skin: SKIN.olive, hair: HAIR.brown, style: 'short', hat: 'beanie', hatColor: SHIRT.amber, shirt: SHIRT.slate, bg: BG.rose, closeup: true, mouth: 'wink' },
  { id: 'agent-23', label: 'Agent with a side parting and a tie, ringed', skin: SKIN.fair, hair: HAIR.brown, style: 'sidepart', shirt: SHIRT.indigo, bg: BG.grey, collar: 'tie', bgStyle: 'ring' },
  { id: 'agent-24', label: 'Agent with a headband and square glasses, laughing', skin: SKIN.medium, hair: HAIR.black, style: 'long', hat: 'headband', hatColor: SHIRT.emerald, shirt: SHIRT.emerald, bg: BG.blue, glasses: 'square', mouth: 'laugh', bgStyle: 'dots' },
  { id: 'agent-25', label: 'Agent with white hair and a beard, close-up', skin: SKIN.light, hair: HAIR.white, style: 'short', shirt: SHIRT.teal, bg: BG.lavender, beard: true, closeup: true, headset: 'single' },
  { id: 'agent-26', label: 'Agent with teal curls and a gradient background', skin: SKIN.deep, hair: HAIR.teal, style: 'curly', shirt: SHIRT.orange, bg: '#a5b4fc', bg2: '#fbcfe8', bgStyle: 'gradient', mouth: 'grin' },
  { id: 'agent-27', label: 'Agent in a rose headscarf with round glasses', skin: SKIN.olive, hair: '#e11d48', style: 'hijab', shirt: '#e11d48', bg: BG.sky, glasses: 'round', bgStyle: 'ring' },
  { id: 'agent-28', label: 'Agent with an afro and a lanyard, close-up', skin: SKIN.medium, hair: HAIR.brown, style: 'afro', shirt: SHIRT.sky, bg: BG.amber, collar: 'lanyard', closeup: true },
];

/** Animals and robots wearing the same headset. */
function mascotSvg(bg: string, body: string): string {
  // Shared headset: band, two ear cups, boom mic — the thing that says
  // "support" however odd the wearer.
  const headset = `<g id="hs" fill="none" stroke-linecap="round"><path d="M17 30C17 18 24 12 32 12s15 6 15 18" stroke="${HALO}" stroke-width="5.2"/><path d="M17 30C17 18 24 12 32 12s15 6 15 18" stroke="${INK}" stroke-width="3"/><path d="M47 34c.5 6-3 10-8 10" stroke="${INK}" stroke-width="2.4"/></g><g fill="${INK}" stroke="${HALO}" stroke-width="1.2"><rect x="13.5" y="25" width="7" height="11" rx="3.5"/><rect x="43.5" y="25" width="7" height="11" rx="3.5"/></g><circle cx="39" cy="44" r="2.4" fill="${INK}"/>`;
  return symbolSvg(bg, `<clipPath id="c"><circle cx="32" cy="32" r="32"/></clipPath><g clip-path="url(#c)">${body}${headset}</g>`);
}

const EYES = `<g fill="${INK}"><circle cx="26" cy="30" r="2"/><circle cx="38" cy="30" r="2"/></g>`;

const MASCOTS: Array<{ id: string; label: string; svg: string }> = [
  {
    id: 'mascot-cat',
    label: 'Cat with a headset',
    svg: mascotSvg(
      BG.lemon,
      `<path d="M17 28L20 12l11 10z M47 28L44 12L33 22z" fill="#f4a261"/><path d="M20 25l2-8 6 6z M44 25l-2-8-6 6z" fill="#fbcfe8"/><circle cx="32" cy="33" r="15" fill="#f4a261"/><ellipse cx="32" cy="40" rx="7" ry="4.5" fill="#fde7d2"/>${EYES}<path d="M30.5 37h3l-1.5 1.8z" fill="#f28b82"/><g stroke="${INK}" stroke-width="1" stroke-linecap="round"><path d="M12 38h9M12 42l9-1M52 38h-9M52 42l-9-1"/></g>`,
    ),
  },
  {
    id: 'mascot-dog',
    label: 'Dog with a headset',
    svg: mascotSvg(
      BG.sky,
      `<ellipse cx="17" cy="33" rx="6" ry="11" fill="#8d5524"/><ellipse cx="47" cy="33" rx="6" ry="11" fill="#8d5524"/><circle cx="32" cy="33" r="15" fill="#b5835a"/><ellipse cx="32" cy="40" rx="8" ry="6" fill="#e9c9a8"/>${EYES}<ellipse cx="32" cy="37.5" rx="3" ry="2.2" fill="${INK}"/><path d="M32 39.5v3M29.5 42.5q2.5 2 5 0" stroke="${INK}" stroke-width="1.3" fill="none" stroke-linecap="round"/>`,
    ),
  },
  {
    id: 'mascot-owl',
    label: 'Owl with a headset',
    svg: mascotSvg(
      BG.lavender,
      `<path d="M19 22l2-9 6 7z M45 22l-2-9-6 7z" fill="#6d4c41"/><ellipse cx="32" cy="38" rx="17" ry="22" fill="#8d6e63"/><ellipse cx="32" cy="46" rx="10" ry="12" fill="#d7ccc8"/><circle cx="25" cy="30" r="7.5" fill="#efe6dc"/><circle cx="39" cy="30" r="7.5" fill="#efe6dc"/><g fill="${INK}"><circle cx="25" cy="30" r="3.2"/><circle cx="39" cy="30" r="3.2"/></g><path d="M29.5 36l2.5 4 2.5-4z" fill="#f59e0b"/>`,
    ),
  },
  {
    id: 'mascot-bear',
    label: 'Bear with a headset',
    svg: mascotSvg(
      BG.mint,
      `<circle cx="19" cy="20" r="6.5" fill="#8d5524"/><circle cx="45" cy="20" r="6.5" fill="#8d5524"/><circle cx="19" cy="20" r="3" fill="#d4a373"/><circle cx="45" cy="20" r="3" fill="#d4a373"/><circle cx="32" cy="33" r="15" fill="#8d5524"/><ellipse cx="32" cy="39" rx="7.5" ry="5.5" fill="#d4a373"/>${EYES}<ellipse cx="32" cy="37" rx="2.8" ry="2" fill="${INK}"/><path d="M29.5 41q2.5 2 5 0" stroke="${INK}" stroke-width="1.3" fill="none" stroke-linecap="round"/>`,
    ),
  },
  {
    id: 'mascot-fox',
    label: 'Fox with a headset',
    svg: mascotSvg(
      BG.grey,
      `<path d="M16 30L18 11l12 11z M48 30L46 11L34 22z" fill="#e76f51"/><path d="M19 27l1-10 7 7z M45 27l-1-10-7 7z" fill="#fff" opacity=".85"/><circle cx="32" cy="33" r="15" fill="#e76f51"/><path d="M19 36c3 9 9 12 13 12s10-3 13-12c-4 3-8 4-13 4s-9-1-13-4z" fill="#fff"/>${EYES}<ellipse cx="32" cy="40" rx="2.6" ry="2" fill="${INK}"/>`,
    ),
  },
  {
    id: 'mascot-penguin',
    label: 'Penguin with a headset',
    svg: mascotSvg(
      BG.blue,
      `<ellipse cx="32" cy="38" rx="16" ry="22" fill="#1e293b"/><ellipse cx="32" cy="42" rx="10.5" ry="15" fill="#fff"/><g fill="#fff"><circle cx="26" cy="30" r="4"/><circle cx="38" cy="30" r="4"/></g><g fill="${INK}"><circle cx="26.5" cy="30.5" r="1.8"/><circle cx="38.5" cy="30.5" r="1.8"/></g><path d="M28.5 35l3.5 4 3.5-4z" fill="#f59e0b"/>`,
    ),
  },
  {
    id: 'mascot-panda',
    label: 'Panda with a headset',
    svg: mascotSvg(
      BG.green,
      `<circle cx="19" cy="20" r="6.5" fill="${INK}"/><circle cx="45" cy="20" r="6.5" fill="${INK}"/><circle cx="32" cy="33" r="15" fill="#fff"/><g fill="${INK}"><ellipse cx="25.5" cy="30" rx="4.2" ry="5" transform="rotate(-15 25.5 30)"/><ellipse cx="38.5" cy="30" rx="4.2" ry="5" transform="rotate(15 38.5 30)"/></g><g fill="#fff"><circle cx="26" cy="30" r="1.6"/><circle cx="38" cy="30" r="1.6"/></g><ellipse cx="32" cy="37.5" rx="2.6" ry="2" fill="${INK}"/><path d="M29.5 41q2.5 2 5 0" stroke="${INK}" stroke-width="1.3" fill="none" stroke-linecap="round"/>`,
    ),
  },
  {
    id: 'mascot-bunny',
    label: 'Rabbit with a headset',
    svg: mascotSvg(
      BG.pink,
      `<ellipse cx="25" cy="14" rx="4.5" ry="12" fill="#e5e7eb"/><ellipse cx="39" cy="14" rx="4.5" ry="12" fill="#e5e7eb"/><ellipse cx="25" cy="14" rx="2" ry="8.5" fill="#fbcfe8"/><ellipse cx="39" cy="14" rx="2" ry="8.5" fill="#fbcfe8"/><circle cx="32" cy="34" r="14" fill="#e5e7eb"/>${EYES}<path d="M30.5 37h3l-1.5 1.8z" fill="#f472b6"/><path d="M32 38.8v2.2M29.5 41q2.5 2 5 0" stroke="${INK}" stroke-width="1.2" fill="none" stroke-linecap="round"/>`,
    ),
  },
  {
    id: 'mascot-robot-boxy',
    label: 'Boxy robot with a headset',
    svg: mascotSvg(
      BG.sky,
      `<path d="M32 9v6" stroke="${INK}" stroke-width="2.2" stroke-linecap="round"/><circle cx="32" cy="8" r="2.6" fill="#ef4444"/><rect x="17" y="17" width="30" height="28" rx="6" fill="#94a3b8"/><rect x="20" y="20" width="24" height="22" rx="4" fill="#cbd5e1"/><g fill="${INK}"><rect x="23" y="26" width="7" height="5" rx="1.5"/><rect x="34" y="26" width="7" height="5" rx="1.5"/></g><rect x="25" y="35" width="14" height="3" rx="1.5" fill="${INK}"/><rect x="22" y="45" width="20" height="10" rx="3" fill="#64748b"/>`,
    ),
  },
  {
    id: 'mascot-robot-round',
    label: 'Round robot with a headset',
    svg: mascotSvg(
      BG.violet,
      `<path d="M32 10v5" stroke="${INK}" stroke-width="2.2" stroke-linecap="round"/><circle cx="32" cy="9" r="2.6" fill="#f59e0b"/><circle cx="32" cy="33" r="16" fill="#7c3aed"/><ellipse cx="32" cy="31" rx="11" ry="8" fill="#1e1b4b"/><g fill="#a5f3fc"><circle cx="27" cy="31" r="2.6"/><circle cx="37" cy="31" r="2.6"/></g><path d="M27.5 36q4.5 3 9 0" stroke="#a5f3fc" stroke-width="1.6" fill="none" stroke-linecap="round"/><rect x="23" y="48" width="18" height="10" rx="4" fill="#5b21b6"/>`,
    ),
  },
  {
    id: 'mascot-robot-retro',
    label: 'Retro TV robot with a headset',
    svg: mascotSvg(
      BG.amber,
      `<path d="M26 10l6 6 6-6" fill="none" stroke="${INK}" stroke-width="2" stroke-linecap="round"/><rect x="15" y="16" width="34" height="27" rx="4" fill="#475569"/><rect x="19" y="20" width="26" height="19" rx="2.5" fill="#86efac"/><g fill="${INK}"><circle cx="27" cy="28" r="2.2"/><circle cx="37" cy="28" r="2.2"/></g><path d="M27 33h10" stroke="${INK}" stroke-width="1.8" stroke-linecap="round"/><rect x="21" y="43" width="22" height="4" rx="1" fill="#334155"/><rect x="24" y="47" width="16" height="10" rx="3" fill="#475569"/>`,
    ),
  },
  {
    id: 'mascot-alien',
    label: 'Friendly alien with a headset',
    svg: mascotSvg(
      BG.green,
      `<path d="M24 12l-3-6M40 12l3-6" stroke="#22c55e" stroke-width="2" stroke-linecap="round"/><circle cx="21" cy="6" r="2" fill="#22c55e"/><circle cx="43" cy="6" r="2" fill="#22c55e"/><path d="M18 30c0-11 6-17 14-17s14 6 14 17c0 10-6 17-14 17S18 40 18 30z" fill="#4ade80"/><g fill="${INK}"><ellipse cx="26" cy="30" rx="3.5" ry="5" transform="rotate(15 26 30)"/><ellipse cx="38" cy="30" rx="3.5" ry="5" transform="rotate(-15 38 30)"/></g><path d="M28 40q4 3 8 0" stroke="${INK}" stroke-width="1.5" fill="none" stroke-linecap="round"/><rect x="24" y="47" width="16" height="10" rx="4" fill="#16a34a"/>`,
    ),
  },
];

const W = '#ffffff';
const SYMBOLS: Array<{ id: string; label: string; svg: string }> = [
  { id: 'symbol-headset', label: 'Headset', svg: symbolSvg(SHIRT.indigo, `<path d="M18 37v-7c0-8.5 6-15 14-15s14 6.5 14 15v7" fill="none" stroke="${W}" stroke-width="4" stroke-linecap="round"/><rect x="14" y="33" width="8" height="13" rx="3.5" fill="${W}"/><rect x="42" y="33" width="8" height="13" rx="3.5" fill="${W}"/><path d="M46 46c0 5-4 7.5-10 7.5" fill="none" stroke="${W}" stroke-width="3" stroke-linecap="round"/><circle cx="35" cy="53.5" r="2.8" fill="${W}"/>`) },
  { id: 'symbol-support', label: 'Support agent pictogram', svg: symbolSvg(SHIRT.slate, `<path d="M15 54c0-11 7.5-16 17-16s17 5 17 16z" fill="${W}"/><circle cx="32" cy="25" r="9" fill="${W}"/><path d="M21 26c0-8 5-13 11-13s11 5 11 13" fill="none" stroke="${SHIRT.slate}" stroke-width="2.6" stroke-linecap="round"/><rect x="18.5" y="23" width="4.5" height="8" rx="2" fill="${SHIRT.slate}" stroke="${W}" stroke-width="1.2"/><rect x="41" y="23" width="4.5" height="8" rx="2" fill="${SHIRT.slate}" stroke="${W}" stroke-width="1.2"/><path d="M43.5 31c0 4-2.5 6.5-6 6.5" fill="none" stroke="${SHIRT.slate}" stroke-width="2" stroke-linecap="round"/>`) },
  { id: 'symbol-chat', label: 'Chat bubble', svg: symbolSvg(SHIRT.sky, `<path d="M14 21a7 7 0 0 1 7-7h22a7 7 0 0 1 7 7v13a7 7 0 0 1-7 7H29l-9 8v-8a7 7 0 0 1-6-7z" fill="${W}"/><g fill="${SHIRT.sky}"><circle cx="24.5" cy="27.5" r="2.4"/><circle cx="32" cy="27.5" r="2.4"/><circle cx="39.5" cy="27.5" r="2.4"/></g>`) },
  { id: 'symbol-robot', label: 'Robot', svg: symbolSvg(SHIRT.emerald, `<path d="M32 12v6" stroke="${W}" stroke-width="2.5" stroke-linecap="round"/><circle cx="32" cy="10" r="2.8" fill="${W}"/><rect x="17" y="18" width="30" height="26" rx="8" fill="${W}"/><rect x="12" y="27" width="4.5" height="9" rx="2" fill="${W}"/><rect x="47.5" y="27" width="4.5" height="9" rx="2" fill="${W}"/><g fill="${SHIRT.emerald}"><circle cx="25.5" cy="30" r="3.2"/><circle cx="38.5" cy="30" r="3.2"/><rect x="25" y="37" width="14" height="3.2" rx="1.6"/></g>`) },
  { id: 'symbol-sparkle', label: 'AI sparkle', svg: symbolSvg(SHIRT.violet, `<path d="M30 14c1 11 7 17 18 18c-11 1-17 7-18 18c-1-11-7-17-18-18c11-1 17-7 18-18z" fill="${W}"/><path d="M47 10c.4 4.5 2 6.1 6.5 6.5c-4.5.4-6.1 2-6.5 6.5c-.4-4.5-2-6.1-6.5-6.5c4.5-.4 6.1-2 6.5-6.5z" fill="${W}"/>`) },
  { id: 'symbol-bubbles', label: 'Two chat bubbles', svg: symbolSvg(SHIRT.teal, `<path d="M12 20a6 6 0 0 1 6-6h16a6 6 0 0 1 6 6v9a6 6 0 0 1-6 6H24l-7 6v-6a6 6 0 0 1-5-6z" fill="${W}"/><path d="M28 34h12a6 6 0 0 1 6 6v6a6 6 0 0 1-6 6h-4l-6 5v-5h-2a6 6 0 0 1-6-6v-6" fill="${W}" opacity=".75"/>`) },
  { id: 'symbol-bolt', label: 'Lightning bolt — fast answers', svg: symbolSvg(SHIRT.amber, `<path d="M35 10L18 36h12l-3 18 19-28H34z" fill="${W}"/>`) },
  { id: 'symbol-lifebuoy', label: 'Lifebuoy — help', svg: symbolSvg(SHIRT.red, `<circle cx="32" cy="32" r="18" fill="none" stroke="${W}" stroke-width="9"/><g stroke="${SHIRT.red}" stroke-width="7"><path d="M32 10v12M32 42v12M10 32h12M42 32h12"/></g>`) },
  { id: 'symbol-question', label: 'Question mark', svg: symbolSvg(SHIRT.indigo, `<path d="M23 24c0-6 4-10 9.5-10S42 18 42 23c0 4-2.5 6-5.5 8s-4 4-4 7" fill="none" stroke="${W}" stroke-width="5" stroke-linecap="round"/><circle cx="32.5" cy="48" r="3.5" fill="${W}"/>`) },
  { id: 'symbol-bulb', label: 'Lightbulb — ideas', svg: symbolSvg(SHIRT.orange, `<path d="M32 12a13 13 0 0 0-8 23c2 2 3 4 3 7h10c0-3 1-5 3-7a13 13 0 0 0-8-23z" fill="${W}"/><rect x="27" y="44" width="10" height="3" rx="1.5" fill="${W}"/><rect x="28" y="49" width="8" height="3" rx="1.5" fill="${W}"/>`) },
  { id: 'symbol-heart', label: 'Heart — we care', svg: symbolSvg(SHIRT.pink, `<path d="M32 52l-15-14.5A9 9 0 0 1 32 24a9 9 0 0 1 15 13.5z" fill="${W}"/>`) },
  { id: 'symbol-star', label: 'Star', svg: symbolSvg(SHIRT.amber, `<path d="M32 11l6.3 13.3 14.7 1.8-10.8 10 2.8 14.5L32 43.5l-13 7.1 2.8-14.5-10.8-10 14.7-1.8z" fill="${W}"/>`) },
  { id: 'symbol-bell', label: 'Service bell', svg: symbolSvg(SHIRT.navy, `<path d="M14 44c0-11 8-19 18-19s18 8 18 19z" fill="${W}"/><rect x="12" y="44" width="40" height="5" rx="2.5" fill="${W}"/><rect x="29.5" y="16" width="5" height="7" rx="2" fill="${W}"/><circle cx="32" cy="15" r="3" fill="${W}"/>`) },
  { id: 'symbol-phone', label: 'Phone handset', svg: symbolSvg(SHIRT.emerald, `<path d="M20 14l8 8-4 5c2 6 7 11 13 13l5-4 8 8-5 6C30 52 12 34 14 19z" fill="${W}"/>`) },
  { id: 'symbol-envelope', label: 'Envelope', svg: symbolSvg(SHIRT.sky, `<rect x="12" y="18" width="40" height="28" rx="4" fill="${W}"/><path d="M12 22l20 14 20-14" fill="none" stroke="${SHIRT.sky}" stroke-width="3" stroke-linejoin="round"/>`) },
  { id: 'symbol-shield', label: 'Shield with a check', svg: symbolSvg(SHIRT.teal, `<path d="M32 10l18 6v14c0 11-8 19-18 24-10-5-18-13-18-24V16z" fill="${W}"/><path d="M23 32l6 6 12-12" fill="none" stroke="${SHIRT.teal}" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"/>`) },
  { id: 'symbol-clock', label: 'Clock — always on', svg: symbolSvg(SHIRT.violet, `<circle cx="32" cy="32" r="18" fill="none" stroke="${W}" stroke-width="4"/><path d="M32 21v12l8 5" fill="none" stroke="${W}" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"/>`) },
  { id: 'symbol-globe', label: 'Globe', svg: symbolSvg(SHIRT.indigo, `<g fill="none" stroke="${W}" stroke-width="3"><circle cx="32" cy="32" r="18"/><ellipse cx="32" cy="32" rx="8" ry="18"/><path d="M14 32h36M17 23h30M17 41h30"/></g>`) },
  { id: 'symbol-wand', label: 'Magic wand', svg: symbolSvg(SHIRT.pink, `<path d="M16 48L40 24" stroke="${W}" stroke-width="5" stroke-linecap="round"/><path d="M44 12c.4 4 2 5.6 6 6c-4 .4-5.6 2-6 6c-.4-4-2-5.6-6-6c4-.4 5.6-2 6-6z M22 14c.3 2.5 1.2 3.4 3.7 3.7c-2.5.3-3.4 1.2-3.7 3.7c-.3-2.5-1.2-3.4-3.7-3.7c2.5-.3 3.4-1.2 3.7-3.7z M50 34c.3 2.5 1.2 3.4 3.7 3.7c-2.5.3-3.4 1.2-3.7 3.7c-.3-2.5-1.2-3.4-3.7-3.7c2.5-.3 3.4-1.2 3.7-3.7z" fill="${W}"/>`) },
  { id: 'symbol-smile', label: 'Smiley face', svg: symbolSvg(SHIRT.amber, `<circle cx="32" cy="32" r="18" fill="${W}"/><g fill="${SHIRT.amber}"><circle cx="25.5" cy="28" r="2.6"/><circle cx="38.5" cy="28" r="2.6"/></g><path d="M23 36c3 6 15 6 18 0" fill="none" stroke="${SHIRT.amber}" stroke-width="3" stroke-linecap="round"/>`) },
  { id: 'symbol-info', label: 'Information', svg: symbolSvg(SHIRT.slate, `<circle cx="32" cy="18" r="4" fill="${W}"/><rect x="28" y="26" width="8" height="22" rx="3" fill="${W}"/>`) },
  { id: 'symbol-check', label: 'Check mark', svg: symbolSvg(SHIRT.emerald, `<path d="M18 33l9 9 19-20" fill="none" stroke="${W}" stroke-width="6" stroke-linecap="round" stroke-linejoin="round"/>`) },
  { id: 'symbol-megaphone', label: 'Megaphone', svg: symbolSvg(SHIRT.orange, `<path d="M14 27v10a3 3 0 0 0 3 3h5l16 8V16l-16 8h-5a3 3 0 0 0-3 3z" fill="${W}"/><path d="M44 25c3 2 3 12 0 14" fill="none" stroke="${W}" stroke-width="3" stroke-linecap="round"/><path d="M21 40l3 9h5l-2-9z" fill="${W}"/>`) },
  { id: 'symbol-plane', label: 'Paper plane — send', svg: symbolSvg(SHIRT.sky, `<path d="M12 30L52 14 42 52l-12-12z M30 40l22-26" fill="${W}" stroke="${SHIRT.sky}" stroke-width="1.5" stroke-linejoin="round"/>`) },
  { id: 'symbol-mic', label: 'Microphone', svg: symbolSvg(SHIRT.red, `<rect x="25" y="12" width="14" height="24" rx="7" fill="${W}"/><path d="M19 30c0 8 6 13 13 13s13-5 13-13" fill="none" stroke="${W}" stroke-width="3.5" stroke-linecap="round"/><path d="M32 43v8M25 51h14" stroke="${W}" stroke-width="3.5" stroke-linecap="round"/>`) },
  { id: 'symbol-rocket', label: 'Rocket', svg: symbolSvg(SHIRT.indigo, `<path d="M32 10c8 6 10 16 8 26H24c-2-10 0-20 8-26z" fill="${W}"/><circle cx="32" cy="26" r="4" fill="${SHIRT.indigo}"/><path d="M24 32l-7 8 8-1z M40 32l7 8-8-1z" fill="${W}"/><path d="M28 40l4 12 4-12z" fill="#f59e0b"/>`) },
  { id: 'symbol-book', label: 'Open book — knowledge', svg: symbolSvg(SHIRT.teal, `<path d="M32 20c-5-4-12-4-18-2v28c6-2 13-2 18 2c5-4 12-4 18-2V18c-6-2-13-2-18 2z" fill="${W}"/><path d="M32 20v28" stroke="${SHIRT.teal}" stroke-width="2"/>`) },
  { id: 'symbol-typing', label: 'Typing dots', svg: symbolSvg(SHIRT.slate, `<g fill="${W}"><circle cx="20" cy="32" r="5"/><circle cx="32" cy="32" r="5"/><circle cx="44" cy="32" r="5"/></g>`) },
  { id: 'symbol-chat-heart', label: 'Chat bubble with a heart', svg: symbolSvg(SHIRT.red, `<path d="M14 21a7 7 0 0 1 7-7h22a7 7 0 0 1 7 7v13a7 7 0 0 1-7 7H29l-9 8v-8a7 7 0 0 1-6-7z" fill="${W}"/><path d="M32 34l-7-6.5a4.2 4.2 0 0 1 7-4.5a4.2 4.2 0 0 1 7 4.5z" fill="${SHIRT.red}"/>`) },
  { id: 'symbol-compass', label: 'Compass', svg: symbolSvg(SHIRT.navy, `<circle cx="32" cy="32" r="18" fill="none" stroke="${W}" stroke-width="3.5"/><path d="M40 24l-5 11-11 5 5-11z" fill="${W}"/><circle cx="32" cy="32" r="2" fill="${SHIRT.navy}"/>`) },
];

/** Every icon the gallery offers: agents, then mascots, then symbols. */
export const ASSISTANT_ICONS: readonly AssistantIcon[] = [
  ...AGENTS.map(({ id, label, ...options }) => ({ id, label, kind: 'agent' as const, url: svgDataUrl(agentAvatarSvg(options)) })),
  ...MASCOTS.map(({ id, label, svg }) => ({ id, label, kind: 'mascot' as const, url: svgDataUrl(svg) })),
  ...SYMBOLS.map(({ id, label, svg }) => ({ id, label, kind: 'symbol' as const, url: svgDataUrl(svg) })),
];

/** What a new bot starts with — see server/src/bots/repo.ts's createBot. */
export const DEFAULT_ASSISTANT_ICON_ID = 'agent-1';
export const DEFAULT_ASSISTANT_ICON_URL = ASSISTANT_ICONS.find((icon) => icon.id === DEFAULT_ASSISTANT_ICON_ID)!.url;
